import classnames from 'classnames';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import DeleteDataModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import DataProductModal from 'v2/DataProducts/DataProductModal/DataProductModal';
import { DataProductEntity, DataProductFormData } from 'v2/DataProducts/models';
import { StrapiApiError } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import DataProductItemV2 from './DataProductItemV2';
import styles from './dataProductsV2.module.scss';

interface DataProductsV2Props {
  isAdmin: boolean;
}

const DataProductsV2 = ({ isAdmin }: DataProductsV2Props) => {
  const [showDataProductModal, setShowDataProductModal] = useState(false);
  const [dataToEdit, setDataToEdit] = useState<DataProductFormData | null>(
    null
  );
  const [itemToRemove, setItemToRemove] = useState<DataProductEntity | null>(
    null
  );

  const queryClient = useQueryClient();
  const { data: dataProducts, isLoading } = useQuery(
    ['dataProducts'],
    async () => {
      const response =
        await restFactory.get<DataProductEntity[]>('data-products');
      return response;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading data products. ${error.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-5">
        <h4 className={styles.title}>Data products</h4>
        {isAdmin ? (
          <Button
            className={classnames('btn-secondary-dark')}
            variant="secondary"
            onClick={() => setShowDataProductModal(true)}
          >
            Add Data Products
          </Button>
        ) : null}
      </div>

      {dataProducts?.map((item) => (
        <DataProductItemV2
          key={item.name}
          data={item}
          isAdmin={isAdmin}
          onEdit={(data) => {
            setDataToEdit(data);
            setShowDataProductModal(true);
          }}
          onDelete={(data) => setItemToRemove(data)}
        />
      ))}

      {!isLoading && !dataProducts?.length ? (
        <h6 className="text-light4 text-center">
          You don&apos;t have products yet.
        </h6>
      ) : null}

      <DataProductModal
        key={`${showDataProductModal}`}
        show={showDataProductModal}
        data={dataToEdit}
        title={dataToEdit ? 'Data Product Details' : 'Add Data Product'}
        onHide={(refreshData) => {
          if (refreshData) {
            queryClient.invalidateQueries('dataProducts');
          }
          setShowDataProductModal(false);
          setDataToEdit(null);
        }}
      />

      <DeleteDataModal
        show={!!itemToRemove}
        onCancel={() => setItemToRemove(null)}
        onOk={async () => {
          await restFactory.delete(`data-products/${itemToRemove?.id}`);
          queryClient.invalidateQueries('dataProducts');
          setItemToRemove(null);
        }}
        okBtnText="Delete"
        title="Delete Data Product"
      >
        <>
          Are you sure you want to delete
          <strong> {itemToRemove?.name}</strong>?
        </>
      </DeleteDataModal>
    </div>
  );
};

export default DataProductsV2;
